import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Input, Spinner } from '@/components/ui';
import { AppHeader } from '@/components/Layout/AppHeader';
import { FeedbackButton } from '@/components/Feedback/FeedbackButton';
import { FeedbackModal } from '@/components/Feedback/FeedbackModal';
import { useOrgStore } from '@/stores/orgStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  templateHubService,
  type TemplateListItem,
} from '@/services/templateHubService';
import { TemplateDetailPanel } from './TemplateDetailPanel';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const TemplateIcon = ({ className }: { className?: string }) => (
  <svg className={cn('w-5 h-5', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Template list item card
// ---------------------------------------------------------------------------

function TemplateCard({
  template,
  isActive,
  onClick,
}: {
  template: TemplateListItem;
  isActive: boolean;
  onClick: () => void;
}) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left p-3 rounded-lg border transition-all',
        isActive
          ? 'border-primary-400 bg-primary-50/60 shadow-sm'
          : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
      )}
      aria-current={isActive ? 'true' : undefined}
    >
      <div className="font-medium text-sm text-gray-900 truncate">{template.name}</div>
      {template.description && (
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{template.description}</p>
      )}
      <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
        {template.source_canvas_name && (
          <span className="truncate max-w-[120px]">{template.source_canvas_name}</span>
        )}
        {template.source_canvas_name && <span>·</span>}
        <span>{formatDate(template.updated_at)}</span>
        {!template.source_node_exists && (
          <span className="text-warning-600 font-medium" title="Source node was deleted">⚠</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
      <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mb-4">
        <TemplateIcon className="w-8 h-8 text-primary-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-1">No templates yet</h3>
      <p className="text-sm text-gray-500 max-w-xs">
        Create templates by right-clicking a Generate node on any canvas and selecting "Save as Template".
      </p>
      <Link
        to="/dashboard"
        className="mt-4 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors"
      >
        Go to Dashboard →
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TemplatesPage() {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const notify = useNotificationStore();

  // Data state
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  // Load templates
  useEffect(() => {
    if (!orgId) return;
    setIsLoading(true);
    templateHubService
      .list()
      .then(setTemplates)
      .catch((err) => {
        notify.error(err.message || 'Failed to load templates');
      })
      .finally(() => setIsLoading(false));
  }, [orgId]);

  // Filtered list
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q))
    );
  }, [templates, searchQuery]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId]
  );

  // Handle template update (from detail panel inline edits)
  const handleTemplateUpdated = useCallback((updated: TemplateListItem) => {
    setTemplates((prev) =>
      prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
    );
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppHeader
        rightExtra={
          <button
            onClick={() => setShowFeedback(true)}
            className="hidden sm:inline text-sm text-gray-500 hover:text-violet-600 transition-colors"
          >
            Need Help?
          </button>
        }
      />

      {/* Main content — side-by-side on md+, stacked (one visible) on mobile */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — Template list (hidden on mobile when detail is open) */}
        <aside
          className={cn(
            'border-r border-gray-200 bg-white flex flex-col shrink-0',
            'w-full md:w-80 lg:w-96',
            selectedId ? 'hidden md:flex' : 'flex'
          )}
        >
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<SearchIcon />}
              className="h-9 text-sm"
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="md" />
              </div>
            ) : filtered.length === 0 && templates.length === 0 ? (
              <EmptyState />
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">
                No templates match "{searchQuery}"
              </div>
            ) : (
              filtered.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  isActive={t.id === selectedId}
                  onClick={() => setSelectedId(t.id)}
                />
              ))
            )}
          </div>

          {/* Count */}
          {!isLoading && templates.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-400">
              {filtered.length} of {templates.length} template{templates.length !== 1 ? 's' : ''}
            </div>
          )}
        </aside>

        {/* Right panel — Detail (hidden on mobile when no selection) */}
        <main
          className={cn(
            'flex-1 min-w-0 bg-white',
            selectedId ? 'flex flex-col' : 'hidden md:flex md:flex-col'
          )}
        >
          {selectedTemplate ? (
            <TemplateDetailPanel
              key={selectedTemplate.id}
              template={selectedTemplate}
              onTemplateUpdated={handleTemplateUpdated}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 px-8">
              <TemplateIcon className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-sm">Select a template to view details and generate</p>
            </div>
          )}
        </main>
      </div>

      {/* Feedback */}
      <FeedbackButton />
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
